/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { LucideBookmark, LucideCircleAlert, LucideCircleCheck, LucideDynamicIcon, LucidePencil, LucideTrash2 } from '@lucide/angular';
import { TranslocoModule } from '@ngneat/transloco';
import { ImageFallbackDirective } from '../../directives/image-fallback.directive';
import { ButtonComponent } from '../../styleguide/components/buttons/default/button.component';
import { V3ContentCardItem, V3ContentCardRow, V3ContentCardView } from './v3-content-card.model';

@Component({
  selector: 'ptl-v3-content-card',
  standalone: true,
  imports: [LucideDynamicIcon, TranslocoModule, ImageFallbackDirective, ButtonComponent],
  templateUrl: './v3-content-card.component.html',
  styleUrls: ['./v3-content-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V3ContentCardComponent {
  item = input.required<V3ContentCardItem>();
  view = input<V3ContentCardView>('grid');
  saveable = input(false);
  saved = input(false);
  saveLabel = input('');
  progressSuffix = input('');
  /** Rows kept (invisible) even when this card has nothing for them — see `v3ContentCardReservedRows`. */
  reserveRows = input<readonly V3ContentCardRow[]>([]);
  editActions = input(false);
  deletable = input(true);
  compact = input(false);

  cardClicked = output<V3ContentCardItem>();
  saveToggled = output<V3ContentCardItem>();
  editClicked = output<V3ContentCardItem>();
  deleteClicked = output<V3ContentCardItem>();

  protected readonly LucideBookmark = LucideBookmark;
  protected readonly LucidePencil = LucidePencil;
  protected readonly LucideTrash2 = LucideTrash2;

  protected readonly showSave = computed(() => this.saveable() && !this.editActions());

  protected readonly isList = computed(() => this.view() === 'list');
  protected readonly hasThumbnail = computed(() => !!this.item().thumbnail);
  protected readonly coverBackground = computed(() => (this.hasThumbnail() ? null : this.item().gradient || null));
  protected readonly gradeLabel = computed(() => this.item().gradeLabel ?? null);
  protected readonly gradePassing = computed(() => this.item().gradePassing !== false);
  protected readonly gradeIcon = computed(() => (this.gradePassing() ? LucideCircleCheck : LucideCircleAlert));
  protected readonly hasProgress = computed(
    () => !this.gradeLabel() && this.item().progress !== null && this.item().progress !== undefined,
  );
  protected readonly progressValue = computed(() => Math.min(100, Math.max(0, Math.round(this.item().progress ?? 0))));
  protected readonly overdueBadge = computed(() => (this.isList() ? null : (this.item().overdueBadge ?? null)));
  // The overdue chip owns the corner; a card carrying both would stack them on top of each other.
  protected readonly coverBadge = computed(() => (this.isList() || this.item().overdueBadge ? null : (this.item().coverBadge ?? null)));
  /** The list view's 64px thumbnail has no room for cover chips, so they lead the meta row instead. */
  protected readonly overdueInList = computed(() => (this.isList() ? (this.item().overdueBadge ?? null) : null));
  protected readonly metaEntries = computed(() => {
    const entries = this.item().meta ?? [];
    const demoted = this.isList() ? this.item().coverBadge : null;
    return demoted ? [demoted, ...entries] : entries;
  });
  protected readonly metaBadge = computed(() => this.item().metaBadge ?? null);
  protected readonly showMetaRow = computed(() => !!this.metaEntries().length || !!this.metaBadge() || !!this.overdueInList());
  protected readonly tags = computed(() => this.item().tags ?? []);
  protected readonly showTagRow = computed(() => !this.isList() && !!this.tags().length);
  protected readonly showBadgeRow = computed(() => !!this.item().statusBadge || (this.isList() && !!this.item().typeLabel));

  private readonly reserved = computed(() => new Set(this.reserveRows()));
  protected readonly reservesBadges = computed(() => this.reserved().has('badges'));
  protected readonly reservesSubtitle = computed(() => this.reserved().has('subtitle'));
  protected readonly reservesMeta = computed(() => this.reserved().has('meta'));
  protected readonly reservesTags = computed(() => !this.isList() && this.reserved().has('tags'));
  protected readonly reservesProgress = computed(() => this.reserved().has('progress'));

  onCardClick(): void {
    this.cardClicked.emit(this.item());
  }

  onSaveClick(): void {
    this.saveToggled.emit(this.item());
  }

  onEditClick(): void {
    this.editClicked.emit(this.item());
  }

  onDeleteClick(): void {
    this.deleteClicked.emit(this.item());
  }
}
